/**
 * Reply draft: a text I2Q wrote from the case, for sales to copy.
 *
 * "Prepare the reply draft (about 30 s)" asks `POST /cases/{id}/answer-draft` (the server
 * answers from its cache when it can); the cached draft is read with `GET`. Without a
 * draft the panel says drafts are prepared on request in shadow mode (MD-05). A draft
 * I2Q prepared when the mail arrived says "Prepared by I2Q on arrival". The server says
 * whether the newest draft
 * still fits the case (`freshness`, review F06, 11 Oct 2026): after a changed customer, an
 * item, a quantity, a price, an undone decision or a re-evaluation it is `stale`, and the
 * panel says "Made before your changes", names what changed and offers "Prepare again"
 * as the main action. The page passes a `revision` of the case; when it changes the
 * panel reads the freshness again. The workbench and the Outlook pane share this.
 *
 * The text is shown once, with the gaps to fill highlighted; "Edit text" turns that same
 * block into an editable box. Edits stay on this page only and are never saved or sent:
 * the panel says so while editing, so sales copy the reply before leaving the case.
 * "Copy reply" copies exactly that text and the subject as plain text, with the gaps to
 * fill kept. The panel says when no draft is prepared yet, while one is being prepared,
 * why preparing failed (360 simulation P16) and, separately, when saved replies could not
 * be read (review F10): a 503 or no connection is not "Not prepared yet", it says so with
 * "Try again" and keeps the reply it already showed. Open questions, the gaps to fill
 * (placeholders), the statements with their source and the verifier's warnings are listed
 * under the text.
 * The quotation the text carries (item, quantity, unit price, amount, lead time, open
 * points; decision of 11 Oct 2026) is shown once more as a table so it can be checked at
 * a glance.
 *
 * Nothing is sent: there is no send, reply or Outlook-draft control in this panel, and
 * the server has no such route.
 */

import { Copy, Icon, RefreshCw, Sparkles } from "@norican/icons";
import {
  Alert,
  Badge,
  Button,
  Card,
  Skeleton,
  Textarea,
  Tooltip,
  Typography,
} from "@norican/react";
import { useMutationState, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { GiveFeedbackButton } from "@/features/feedback";
import { useShortcutHandler } from "@/hooks/use-shortcuts";
import {
  type AnswerDraft,
  answerDraftCreateKey,
  answerDraftFailureText,
  answerDraftReadFailureText,
  type AnswerQuotation,
  type AnswerQuotationRow,
  draftKindLabel,
  draftStaleText,
  emptyDraftText,
  isDraftStale,
  languageLabel,
  NOTHING_SENT_NOTE,
  personName,
  quotationRowText,
  sendCaseActivity,
  splitPlaceholders,
  useAnswerDraft,
  useCreateAnswerDraft,
} from "@/lib/api";
import { FEATURES } from "@/lib/features";
import { logger } from "@/lib/logger";
import {
  DRAFT_ON_REQUEST_TEXT,
  MADE_BEFORE_CHANGES,
  PREPARE_DRAFT_LABEL,
  PREPARED_BY,
  PREPARED_ON_ARRIVAL,
  replyClipboardText,
} from "@/lib/sales-flow";
import { track } from "@/lib/usage";
import { formatDayTime } from "@/lib/work-labels";

function texts(raw: unknown): string[] {
  return Array.isArray(raw)
    ? raw.filter((entry): entry is string => typeof entry === "string" && entry.trim() !== "")
    : [];
}

/** The body with the gaps sales have to fill marked. Read only. */
function MarkedBody({ body, placeholders }: { body: string; placeholders: string[] }) {
  return (
    <p className="text-fg bg-bg-inset rounded-md p-3 text-sm break-words whitespace-pre-wrap">
      {splitPlaceholders(body, placeholders).map((segment, index) =>
        segment.placeholder ? (
          <span
            key={index}
            className="bg-warning-subtle text-warning-fg rounded px-0.5 font-semibold"
          >
            {segment.text}
          </span>
        ) : (
          <span key={index}>{segment.text}</span>
        ),
      )}
    </p>
  );
}

/** Under the box while editing: edits are not saved anywhere. */
export const EDITS_STAY_HERE_TEXT =
  "Your edits stay on this page only. I2Q does not save them: copy the reply before you leave this case.";

/** The editable text and the copy button. Keyed on the draft, so a new draft starts fresh. */
function DraftEditor({ draft, stale = false }: { draft: AnswerDraft; stale?: boolean }) {
  const original = draft.body ?? "";
  const [text, setText] = useState(original);
  // One view of the text: highlighted gaps to read, or the box to edit. Never both.
  const [editing, setEditing] = useState(false);
  const [copied, setCopied] = useState<"yes" | "failed" | null>(null);
  const placeholders = texts(draft.placeholders);
  const open = placeholders.filter((token) => text.includes(token));

  const copy = async () => {
    if (text.trim() === "") {
      return;
    }
    try {
      await navigator.clipboard.writeText(
        FEATURES.copyReady ? replyClipboardText(draft.subject, text) : text,
      );
      setCopied("yes");
      logger.debug("[case] reply draft copied", { case: draft.case_id });
      void sendCaseActivity(draft.case_id, {
        kind: "reply_copy",
        prepared_draft: draft.created_by === PREPARED_BY,
      });
      track(
        "case.reply_copy",
        { prepared: draft.created_by === PREPARED_BY, edited: text !== original },
        { caseId: draft.case_id },
      );
    } catch (error) {
      logger.warn("[case] copy failed", error);
      setCopied("failed");
    }
  };
  useShortcutHandler("copyReply", text.trim() !== "" ? () => void copy() : null);

  return (
    <div className="flex flex-col gap-2">
      {draft.subject && (
        <Typography variant="body-sm" className="text-fg break-words">
          <span className="text-fg-subtle">Subject: </span>
          {draft.subject}
        </Typography>
      )}
      {editing ? (
        <Textarea
          rows={10}
          autoResize
          value={text}
          onChange={(event) => {
            setText(event.target.value);
            setCopied(null);
          }}
          aria-label="Reply text"
        />
      ) : (
        <MarkedBody body={text} placeholders={open} />
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          tone={stale ? "neutral" : "accent"}
          variant={stale ? "outline" : "solid"}
          startIcon={<Icon icon={Copy} size="xs" />}
          disabled={text.trim() === ""}
          onClick={() => void copy()}
        >
          {stale ? "Copy old reply" : "Copy reply"}
        </Button>
        <Button size="sm" variant="outline" tone="neutral" onClick={() => setEditing(!editing)}>
          {editing ? "Done editing" : "Edit text"}
        </Button>
        {text !== original && (
          <Button size="sm" variant="ghost" tone="neutral" onClick={() => setText(original)}>
            Undo my edits
          </Button>
        )}
        {copied === "yes" && (
          <Typography variant="caption" tone="muted" role="status">
            Copied with the subject. Paste it into your reply in Outlook.
          </Typography>
        )}
        {copied === "failed" && (
          <Typography variant="caption" className="text-danger-fg" role="alert">
            Copy did not work. Mark the text and copy it yourself.
          </Typography>
        )}
      </div>
      {(editing || text !== original) && (
        <Typography variant="caption" tone="muted" data-edits-local>
          {EDITS_STAY_HERE_TEXT}
        </Typography>
      )}
      {open.length > 0 && (
        <div className="flex flex-col gap-1">
          <Typography variant="caption" tone="subtle">
            Fill these in before you send the reply
          </Typography>
          <div className="flex flex-wrap gap-1">
            {open.map((token) => (
              <Badge key={token} tone="warning" variant="outline" size="sm">
                {token}
              </Badge>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * The lead time of one quotation row, as the table shows it. A lead time from the item
 * master is "about" in the reply text, so the table says the same and never shows it as
 * firm (review of the ease round, 13 Oct 2026).
 */
export function quotationLeadText(
  row: Pick<AnswerQuotationRow, "lead_time" | "lead_approx">,
): string {
  if (!row.lead_time) {
    return "lead time to confirm";
  }
  return row.lead_approx
    ? `lead time about ${row.lead_time}, to be confirmed`
    : `lead time ${row.lead_time}`;
}

/** The quotation inside the reply text, as a table to check at a glance (read only). */
export function QuotationPreview({ quotation }: { quotation: AnswerQuotation | null | undefined }) {
  const rows = quotation?.rows ?? [];
  const open = quotation?.open_points ?? [];
  if (rows.length === 0 && open.length === 0) {
    return null;
  }
  return (
    <div className="flex flex-col gap-1" data-quotation-preview>
      <Typography variant="caption" tone="subtle">
        The quotation in the text
      </Typography>
      {rows.length > 0 && (
        <ul className="border-border-subtle flex flex-col rounded-md border text-xs">
          {rows.map((row) => (
            <li
              key={row.line}
              className="border-border-subtle flex flex-wrap items-baseline gap-x-2 border-b px-2 py-1 last:border-b-0"
            >
              <span className="text-fg-subtle tabular-nums">{row.line}.</span>
              <span className="text-fg font-mono font-semibold">{row.item}</span>
              {row.description && <span className="text-fg-muted">{row.description}</span>}
              <span className="text-fg tabular-nums">{quotationRowText(row)}</span>
              <span
                className={row.lead_time && !row.lead_approx ? "text-fg-muted" : "text-warning-fg"}
                data-lead-approx={row.lead_approx ? "" : undefined}
              >
                {quotationLeadText(row)}
              </span>
            </li>
          ))}
          {quotation?.total && quotation.total_currency && (
            <li className="text-fg flex justify-end px-2 py-1 font-semibold tabular-nums">
              Total {quotation.total_currency} {quotation.total}
            </li>
          )}
        </ul>
      )}
      {open.length > 0 && (
        <ul className="flex flex-col gap-0.5 text-xs">
          {open.map((point) => (
            <li key={point.line} className="text-fg-muted break-words">
              Open point, line {point.line} ({point.text}): {(point.missing ?? []).join(", ")}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** The open questions of a draft for sales (counted in "Details (n)"). */
export function openQuestionsOf(
  draft: Pick<AnswerDraft, "open_questions_for_rep"> | null,
): string[] {
  return draft ? texts(draft.open_questions_for_rep) : [];
}

/** What the draft rests on, the questions for sales and the warnings. */
export function DraftDetails({ draft }: { draft: AnswerDraft }) {
  const questions = texts(draft.open_questions_for_rep);
  const claims = (Array.isArray(draft.claims) ? draft.claims : []).filter(
    (claim) => claim && typeof claim.text === "string" && claim.text.trim() !== "",
  );
  const flags = (Array.isArray(draft.flags) ? draft.flags : []).filter(
    (flag) => flag && typeof flag.token === "string" && flag.token.trim() !== "",
  );
  return (
    <>
      {flags.length > 0 && (
        <Alert tone="warning" title="Check before you use it">
          <ul className="flex flex-col gap-0.5">
            {flags.map((flag, index) => (
              <li key={index} className="break-words">
                Check this: {flag.token}
                {flag.reason ? `. ${flag.reason}` : ""}
              </li>
            ))}
          </ul>
        </Alert>
      )}
      {questions.length > 0 && (
        <div className="flex flex-col gap-1">
          <Typography variant="caption" tone="subtle">
            Open questions for you
          </Typography>
          <ul className="flex list-disc flex-col gap-0.5 pl-5">
            {questions.map((question, index) => (
              <li key={index} className="text-fg text-sm break-words">
                {question}
              </li>
            ))}
          </ul>
        </div>
      )}
      {claims.length > 0 && (
        <div className="flex flex-col gap-1">
          <Typography variant="caption" tone="subtle">
            What the text states, and where it comes from
          </Typography>
          <ul className="flex flex-col gap-0.5">
            {claims.map((claim, index) => (
              <li key={index} className="text-fg-muted text-xs break-words">
                {claim.source ? (
                  <Tooltip.Root>
                    <Tooltip.Trigger className="cursor-help text-left underline decoration-dotted">
                      {claim.text}
                    </Tooltip.Trigger>
                    <Tooltip.Portal>
                      <Tooltip.Positioner side="top">
                        <Tooltip.Popup className="max-w-xs">
                          Source: {claim.source}
                          <Tooltip.Arrow />
                        </Tooltip.Popup>
                      </Tooltip.Positioner>
                    </Tooltip.Portal>
                  </Tooltip.Root>
                ) : (
                  claim.text
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
}

/** What the panel shows: one state at a time. */
export type DraftPanelState =
  | "loading"
  | "preparing"
  | "failed"
  | "unreadable"
  | "none"
  | "empty"
  | "draft";

/**
 * The state of the panel from the reads and the request to prepare a draft.
 *
 * `readFailed`: reading the saved reply failed (503, no connection). Without a reply
 * already shown that is "unreadable", never "none": a saved reply may exist (review F10).
 * With a reply already shown the reply stays, and the panel adds a notice.
 */
export function draftPanelState(facts: {
  loading: boolean;
  preparing: boolean;
  failed: boolean;
  hasDraft: boolean;
  empty: boolean;
  readFailed?: boolean;
}): DraftPanelState {
  if (facts.loading) {
    return "loading";
  }
  if (facts.preparing) {
    return "preparing";
  }
  if (facts.failed) {
    return "failed";
  }
  if (!facts.hasDraft) {
    return facts.readFailed ? "unreadable" : "none";
  }
  return facts.empty ? "empty" : "draft";
}

/** The text of a case without a draft yet. */
export const NOT_PREPARED_TEXT =
  "Not prepared yet. I2Q prepares the reply for new cases that are ready to quote. Press the button to prepare it from the lines, the prices and the customer. Then copy it into Outlook and check it there.";
/** The text while a draft is being prepared. */
export const PREPARING_TEXT = "Preparing the reply. This can take up to a minute.";

/** "Could not prepare the reply (<reason>). Try again." */
export function draftFailedText(reason: string): string {
  const trimmed = reason.trim().replace(/[.\s]+$/, "");
  return `Could not prepare the reply (${trimmed}). Try again.`;
}

/** True when the draft was made before `changedAt` (a later customer or line decision). */
export function draftOutdated(
  createdAt: string | null | undefined,
  changedAt: string | null | undefined,
): boolean {
  const made = createdAt ? new Date(createdAt).getTime() : Number.NaN;
  const changed = changedAt ? new Date(changedAt).getTime() : Number.NaN;
  return Number.isFinite(made) && Number.isFinite(changed) && changed > made;
}

/** Reads the reply's freshness again when the case's `revision` changes (not on mount). */
export function useRefreshOnRevision(caseId: string, revision: string | null | undefined) {
  const queryClient = useQueryClient();
  const seen = useRef(revision);
  useEffect(() => {
    if (revision == null || seen.current === revision) {
      return;
    }
    seen.current = revision;
    logger.debug("[case] case changed, reading the reply's freshness again", { case: caseId });
    void queryClient.invalidateQueries({ queryKey: ["case-answer-draft", caseId] });
  }, [caseId, queryClient, revision]);
}

/** The panel in the case view and in the Outlook pane. */
export function AnswerDraftPanel({
  caseId,
  className,
  changedAt = null,
  revision = null,
  compact = false,
}: {
  caseId: string;
  className?: string;
  /** The last change to the case a reply depends on (customer, line decisions). */
  changedAt?: string | null;
  /** A key of what a reply rests on (`replyRevisionOf`); a change reads freshness again. */
  revision?: string | null;
  /** The Outlook pane: the text and "Copy reply" only, no details. */
  compact?: boolean;
}) {
  const cached = useAnswerDraft(caseId);
  const create = useCreateAnswerDraft(caseId);
  useRefreshOnRevision(caseId, revision);
  const draft = cached.data ?? null;
  const outdated = Boolean(
    draft && (isDraftStale(draft) || draftOutdated(draft.created_at, changedAt)),
  );
  const staleText = draft && outdated ? draftStaleText(draft) : null;
  const prepared = draft?.created_by === PREPARED_BY;
  const empty = draft ? emptyDraftText(draft) : null;
  const language = languageLabel(draft?.language);
  const kind = draftKindLabel(draft?.kind);
  // Every "Prepare reply" of this case (this panel's and the decision strip's), oldest first.
  const runs = useMutationState({
    filters: { mutationKey: answerDraftCreateKey(caseId) },
    select: (mutation) => mutation.state,
  });
  const lastRun = runs[runs.length - 1];
  const preparing = create.isPending || runs.some((run) => run.status === "pending");
  const prepareError = create.error ?? (lastRun?.status === "error" ? lastRun.error : null);
  const failure = prepareError ? answerDraftFailureText(prepareError) : null;
  const readFailure = cached.isError ? answerDraftReadFailureText(cached.error) : null;
  const state = draftPanelState({
    loading: cached.isLoading,
    preparing,
    failed: failure !== null,
    hasDraft: draft !== null,
    empty: empty !== null,
    readFailed: readFailure !== null,
  });
  const retryRead = () => {
    logger.debug("[case] reply draft: read again", { case: caseId });
    void cached.refetch();
  };

  const ask = (refresh: boolean) => {
    logger.debug("[case] reply draft", { case: caseId, refresh });
    create.mutate(refresh);
  };

  return (
    <Card variant="outline" padding="none" className={className} id="reply-draft">
      <Card.Header
        padding="sm"
        className="border-border-subtle flex flex-wrap items-center gap-2 border-b"
      >
        <Typography variant="label">Reply</Typography>
        {prepared && !outdated && (
          <Badge tone="info" variant="subtle" size="sm">
            {PREPARED_ON_ARRIVAL}
          </Badge>
        )}
        {outdated && (
          <Badge tone="warning" variant="outline" size="sm">
            {MADE_BEFORE_CHANGES}
          </Badge>
        )}
        {language && (
          <Badge tone="neutral" variant="outline" size="sm">
            {language}
          </Badge>
        )}
        {kind && (
          <Badge tone="neutral" variant="outline" size="sm">
            {kind}
          </Badge>
        )}
        <Button
          size="sm"
          variant={outdated ? "solid" : "outline"}
          tone={outdated ? "accent" : "neutral"}
          className="ml-auto"
          loading={preparing}
          startIcon={<Icon icon={draft ? RefreshCw : Sparkles} size="xs" />}
          onClick={() => ask(draft !== null)}
        >
          {draft ? "Prepare again" : PREPARE_DRAFT_LABEL}
        </Button>
        {draft && !empty && !compact && (
          <GiveFeedbackButton
            label="Feedback on this reply"
            options={{ surface: "reply_draft", caseId, kind: "model", topic: "reply_draft" }}
          />
        )}
      </Card.Header>
      <Card.Body className="flex flex-col gap-3">
        {state === "loading" && <Skeleton variant="rect" height={96} />}
        {state === "preparing" && (
          <div className="flex flex-col gap-2" aria-busy="true">
            <Typography variant="body-sm" tone="muted" role="status">
              {PREPARING_TEXT}
            </Typography>
            <Skeleton variant="rect" height={96} />
          </div>
        )}
        {state === "failed" && failure && (
          <Alert tone="warning" title="No draft">
            <span className="flex flex-col items-start gap-2">
              <span>{draftFailedText(failure)}</span>
              <Button
                size="sm"
                variant="outline"
                tone="neutral"
                onClick={() => ask(draft !== null)}
              >
                Try again
              </Button>
            </span>
          </Alert>
        )}
        {state === "unreadable" && readFailure && (
          <Alert tone="warning" title="Saved reply not available" data-reply-read-failed>
            <span className="flex flex-col items-start gap-2">
              <span>{readFailure}</span>
              <Button
                size="sm"
                variant="outline"
                tone="neutral"
                loading={cached.isFetching}
                onClick={retryRead}
              >
                Try again
              </Button>
            </span>
          </Alert>
        )}
        {state === "none" && (
          <>
            <Typography variant="body-sm" tone="muted">
              {NOT_PREPARED_TEXT}
            </Typography>
            <Typography variant="caption" tone="muted" data-draft-on-request>
              {DRAFT_ON_REQUEST_TEXT}
            </Typography>
          </>
        )}
        {draft && readFailure && state !== "unreadable" && (
          <Alert tone="info" title="Could not check for a newer reply" data-reply-read-failed>
            <span className="flex flex-col items-start gap-2">
              <span>{readFailure} This is the reply shown before.</span>
              <Button
                size="sm"
                variant="outline"
                tone="neutral"
                loading={cached.isFetching}
                onClick={retryRead}
              >
                Try again
              </Button>
            </span>
          </Alert>
        )}
        {staleText && state !== "preparing" && (
          <Alert tone="warning" title="Made before the case changed" data-reply-stale>
            {staleText}
          </Alert>
        )}
        {state === "empty" && empty && (
          <Alert tone="info" title="No draft text">
            {empty}
          </Alert>
        )}
        {(state === "draft" || (state === "failed" && draft && !empty)) && draft && (
          <>
            <DraftEditor
              key={`${draft.created_at ?? ""}-${draft.body ?? ""}`}
              draft={draft}
              stale={outdated}
            />
            {!compact && <QuotationPreview quotation={draft.quotation} />}
            {!compact && <DraftDetails draft={draft} />}
          </>
        )}
        <Typography variant="caption" tone="muted" className="break-words">
          {NOTHING_SENT_NOTE}
        </Typography>
        {draft?.created_at && (
          <Typography variant="caption" tone="subtle" className="break-words">
            {prepared ? "Prepared by I2Q" : "Drafted"} {formatDayTime(draft.created_at)}
            {draft.created_by && !prepared ? ` for ${personName(draft.created_by)}` : ""}
            {draft.cached ? ", reused from earlier" : ""}.
            {outdated ? " The case changed after that: prepare it again before you use it." : ""}
          </Typography>
        )}
      </Card.Body>
    </Card>
  );
}
